/**
 * StandardServiceDetailPage
 * P2.7-X: Service Detail UX Standardization
 *
 * Generalised in-page booking flow for Bar, Pool, Gym, and Transport.
 * Follows the Restaurant/SPA interaction model:
 *   1. Select option  →  2. Select date & time  →  3. Confirm  →  4. Success
 *
 * Features (matching Restaurant / SPA):
 * - Image slideshow
 * - Local push-to-talk voice (Space key) — prevents global overlay hijacking
 * - TTS feedback via zaxiaVoice
 * - Step progress indicator
 * - Keyboard arrow/enter navigation
 * - Success overlay with auto-return
 * - Voice hints forwarded to parent
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import clsx from 'clsx';
import { Check, Mic, Calendar, Clock, X } from 'lucide-react';
import {
  zaxiaVoice,
  createSpeechRecognition,
  isSpeechRecognitionAvailable,
  type SpeechRecognitionEvent,
} from '@/services';
import { processVoiceInput, createVoiceContext, type VoiceAction } from '@/voice';
import type { CreateBookingRequest } from '@/api';
import type { ServiceDetail, ServiceOption, SessionSlot } from '@/types';

// =============================================================================
// TYPES
// =============================================================================

export interface VoiceHintConfig {
  title: string;
  hints: string[];
  variant?: 'default' | 'action' | 'confirm';
}

interface StandardServiceDetailPageProps {
  service: ServiceDetail;
  isActive: boolean;
  onBack: () => void;
  onBookingComplete: (booking: CreateBookingRequest) => Promise<void>;
  onVoiceHintsChange?: (hints: VoiceHintConfig | null) => void;
  /** P2.7-2: Pre-select an option by optionId (from voice booking command) */
  initialOptionId?: string;
}

type BookingStep = 'select-option' | 'select-datetime' | 'confirm';

interface Booking {
  option: ServiceOption | null;
  date: string | null; // YYYY-MM-DD
  time: string | null; // HH:MM
  sessionLabel: string | null;    // e.g. 'Full Day', 'Morning', 'Afternoon'
  sessionDuration: number | null; // overrides option.durationMinutes for sessions
  sessionPrice: number | null;    // per-slot price, overrides option.price for sessions
}

// =============================================================================
// HELPERS
// =============================================================================

function generateTimeSlots(): string[] {
  const slots: string[] = [];
  for (let hour = 8; hour <= 21; hour++) {
    slots.push(`${hour.toString().padStart(2, '0')}:00`);
  }
  return slots;
}

const TIME_SLOTS = generateTimeSlots();

function getDefaultDate(): string {
  const today = new Date();
  return today.toISOString().split('T')[0]!;
}

function generateAvailableDates(): string[] {
  const dates: string[] = [];
  const today = new Date();
  for (let i = 0; i < 7; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    dates.push(d.toISOString().split('T')[0]!);
  }
  return dates;
}

function formatDateDisplay(dateStr: string): string {
  const date = new Date(dateStr + 'T12:00:00');
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const todayStr = today.toISOString().split('T')[0]!;
  const tomorrowStr = tomorrow.toISOString().split('T')[0]!;
  if (dateStr === todayStr) return 'Today';
  if (dateStr === tomorrowStr) return 'Tomorrow';
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function formatTimeDisplay(timeStr: string): string {
  const [h, m] = timeStr.split(':').map(Number);
  const hour = h! % 12 || 12;
  const ampm = h! >= 12 ? 'PM' : 'AM';
  return m ? `${hour}:${m.toString().padStart(2, '0')} ${ampm}` : `${hour} ${ampm}`;
}

function formatCurrency(amount: number): string {
  return amount % 1 === 0 ? `€${amount}` : `€${amount.toFixed(2)}`;
}

function formatDuration(minutes?: number): string | null {
  if (!minutes) return null;
  if (minutes >= 60) {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return m > 0 ? `${h}h ${m}min` : `${h}h`;
  }
  return `${minutes}min`;
}

// =============================================================================
// DATE / TIME VOICE PARSING  (shared subset — not the full restaurant parser)
// =============================================================================

function parseDateFromVoice(transcript: string): string | null {
  const lower = transcript.toLowerCase();
  const today = new Date();

  if (/\btoday\b/.test(lower)) return today.toISOString().split('T')[0]!;

  if (/\btomorrow\b/.test(lower)) {
    const t = new Date(today);
    t.setDate(t.getDate() + 1);
    return t.toISOString().split('T')[0]!;
  }

  // "in X days"
  const inDays = lower.match(/\bin\s+(\d+|two|three|four|five|six|seven)\s+days?\b/i);
  if (inDays) {
    const map: Record<string, number> = { two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7 };
    const n = map[inDays[1]!.toLowerCase()] ?? parseInt(inDays[1]!);
    const d = new Date(today);
    d.setDate(d.getDate() + n);
    return d.toISOString().split('T')[0]!;
  }

  // Day names
  const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  let weekdayDate: string | null = null;
  for (let i = 0; i < dayNames.length; i++) {
    const pat = new RegExp(`\\b(this\\s+)?${dayNames[i]}\\b|\\bnext\\s+${dayNames[i]}\\b`, 'i');
    if (pat.test(lower)) {
      const currentDay = today.getDay();
      let daysUntil = i - currentDay;
      if (daysUntil <= 0) daysUntil += 7;
      if (/\bnext\b/i.test(lower)) daysUntil += 7;
      const d = new Date(today);
      d.setDate(d.getDate() + daysUntil);
      weekdayDate = d.toISOString().split('T')[0]!;
      break;
    }
  }

  // Ordinal dates: "24th", "1st", "23rd" — takes priority over weekday name
  const ordinalMatch = lower.match(/\b(\d{1,2})(?:st|nd|rd|th)\b/i);
  if (ordinalMatch) {
    const targetDay = parseInt(ordinalMatch[1]!);
    const todayStr = today.toISOString().split('T')[0]!;
    const candidate = new Date(today.getFullYear(), today.getMonth(), targetDay);
    if (candidate.toISOString().split('T')[0]! < todayStr) {
      candidate.setMonth(candidate.getMonth() + 1);
    }
    return candidate.toISOString().split('T')[0]!;
  }

  if (weekdayDate) return weekdayDate;

  return null;
}

function parseTimeFromVoice(transcript: string): string | null {
  // Normalize: remove dots from a.m./p.m. so speech-to-text variants like
  // "3:00 p.m." or "3 a.m." are handled the same as "3pm" / "3am".
  const normalised = transcript
    .toLowerCase()
    .replace(/\bp\.m\./g, 'pm')
    .replace(/\ba\.m\./g, 'am')
    .replace(/\s/g, '');

  // "2pm", "2:30pm", "14:30"
  const ampm = normalised.match(/(\d{1,2})(?::(\d{2}))?(am|pm)/);
  if (ampm) {
    let h = parseInt(ampm[1]!);
    const m = ampm[2] ? parseInt(ampm[2]) : 0;
    if (ampm[3] === 'pm' && h < 12) h += 12;
    if (ampm[3] === 'am' && h === 12) h = 0;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
  }

  // 24h "14:30" (anchor-free so it matches inside a longer transcript)
  const h24 = normalised.match(/(?<!\d)(\d{1,2}):(\d{2})(?!\d)/);
  if (h24) {
    const h = parseInt(h24[1]!);
    if (h >= 0 && h <= 23) {
      return `${h.toString().padStart(2, '0')}:${h24[2]}`;
    }
  }

  // Word-based evening times
  const wordMap: [RegExp, string][] = [
    [/\b(seven|7)\b.*\b(evening|pm|tonight)\b/, '19:00'],
    [/\b(eight|8)\b.*\b(evening|pm|tonight)\b/, '20:00'],
    [/\b(nine|9)\b.*\b(evening|pm|tonight)\b/, '21:00'],
    [/\btonight\b/, '20:00'],
  ];
  for (const [pat, val] of wordMap) {
    if (pat.test(transcript.toLowerCase())) return val;
  }

  return null;
}

/** Round HH:MM to nearest slot within ±60min. Returns slot string or null. */
function snapToSlot(time: string): string | null {
  const [h, m] = time.split(':').map(Number);
  const target = (h ?? 0) * 60 + (m ?? 0);
  let best: string | null = null;
  let bestDiff = Infinity;
  for (const slot of TIME_SLOTS) {
    const [sh, sm] = slot.split(':').map(Number);
    const diff = Math.abs((sh ?? 0) * 60 + (sm ?? 0) - target);
    if (diff < bestDiff) { bestDiff = diff; best = slot; }
  }
  return bestDiff <= 60 ? best : null;
}

// =============================================================================
// COMPONENT
// =============================================================================

export function StandardServiceDetailPage({
  service,
  isActive,
  onBack,
  onBookingComplete,
  onVoiceHintsChange,
  initialOptionId,
}: StandardServiceDetailPageProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [bookingStep, setBookingStep] = useState<BookingStep>('select-option');
  const [booking, setBooking] = useState<Booking>({ option: null, date: null, time: null, sessionLabel: null, sessionDuration: null, sessionPrice: null });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmFocusedButton, setConfirmFocusedButton] = useState<'confirm' | 'cancel'>('confirm');
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Remote / keyboard navigation indices
  const [focusedOptionIndex, setFocusedOptionIndex] = useState(0);
  const [focusedDateIndex, setFocusedDateIndex] = useState(0);
  const [focusedTimeIndex, setFocusedTimeIndex] = useState(4); // default ~10:00
  const [focusedSessionIndex, setFocusedSessionIndex] = useState(-1);
  // Which row is active within the select-datetime step
  const [dtSection, setDtSection] = useState<'dates' | 'times' | 'sessions'>('dates');

  // Voice state
  const [voiceState, setVoiceState] = useState<'idle' | 'listening' | 'processing'>('idle');
  const [voiceTranscript, setVoiceTranscript] = useState('');
  const [voiceFeedback, setVoiceFeedback] = useState<string | null>(null);
  const recognitionRef = useRef<ReturnType<typeof createSpeechRecognition>>(null);
  const transcriptRef = useRef('');

  const containerRef = useRef<HTMLDivElement>(null);
  const initialOptionProcessedRef = useRef(false);
  // Timer that auto-navigates back after the booking confirmation TTS.
  const confirmTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep booking + step accessible in voice handlers without stale closures
  const bookingRef = useRef(booking);
  const bookingStepRef = useRef(bookingStep);
  useEffect(() => { bookingRef.current = booking; }, [booking]);
  useEffect(() => { bookingStepRef.current = bookingStep; }, [bookingStep]);

  // Memoize to prevent new array references on every render (would cause infinite
  // re-renders via the voice hints useEffect → onVoiceHintsChange → parent setState).
  const availableOptions = useMemo(
    () => service.options.filter(o => o.available),
    [service.options]
  );
  const availableDates = useMemo(() => generateAvailableDates(), []);

  // ==========================================================================
  // Auto-select option from voice (P2.7-2)
  // ==========================================================================

  useEffect(() => {
    if (!initialOptionId || initialOptionProcessedRef.current || !isActive) return;
    const option = availableOptions.find(o => o.optionId === initialOptionId);
    if (option) {
      initialOptionProcessedRef.current = true;
      setTimeout(() => {
        setBooking(prev => ({ ...prev, option }));
        setBookingStep('select-datetime');
        zaxiaVoice.speak(
          `${option.name} selected. Please choose a date and time.`
        );
      }, 200);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, initialOptionId]);

  // ==========================================================================
  // Voice hints
  // ==========================================================================

  useEffect(() => {
    if (!isActive) return;

    let hints: VoiceHintConfig;
    switch (bookingStep) {
      case 'select-option':
        hints = {
          title: `${service.title} options`,
          hints: availableOptions.slice(0, 3).map(o => o.name),
          variant: 'default',
        };
        break;
      case 'select-datetime':
        hints = {
          title: (booking.option?.sessionSlots ?? service.sessionSlots)?.length ? 'Select date & session' : 'Select date & time',
          hints: (booking.option?.sessionSlots ?? service.sessionSlots)?.length
            ? ['Today', 'Tomorrow', 'Full Day', 'Morning', 'Afternoon']
            : ['Today', 'Tomorrow at 3pm', 'Friday'],
          variant: 'action',
        };
        break;
      case 'confirm':
        hints = {
          title: 'Confirm booking',
          hints: ['Confirm', 'Cancel'],
          variant: 'confirm',
        };
        break;
    }
    onVoiceHintsChange?.(hints);
    return () => onVoiceHintsChange?.(null);
  }, [isActive, bookingStep, booking.option, service.title, service.sessionSlots, availableOptions, onVoiceHintsChange]);

  // ==========================================================================
  // Image slideshow
  // ==========================================================================

  useEffect(() => {
    if (service.images.length <= 1) return;
    const id = setInterval(
      () => setCurrentImageIndex(prev => (prev + 1) % service.images.length),
      5000
    );
    return () => clearInterval(id);
  }, [service.images.length]);

  // Reset confirm button focus each time the user reaches the confirm step
  useEffect(() => {
    if (bookingStep === 'confirm') setConfirmFocusedButton('confirm');
  }, [bookingStep]);

  // ==========================================================================
  // TTS speak helper
  // ==========================================================================

  const speak = useCallback((text: string, onComplete?: () => void) => {
    setVoiceFeedback(text);
    zaxiaVoice.speak(text, {
      onEnd: () => {
        setVoiceFeedback(null);
        onComplete?.();
      },
    });
  }, []);

  // Clear voiceFeedback after timeout if TTS doesn't end callback
  useEffect(() => {
    if (!voiceFeedback) return;
    const id = setTimeout(() => setVoiceFeedback(null), 5000);
    return () => clearTimeout(id);
  }, [voiceFeedback]);

  // ==========================================================================
  // Booking actions
  // ==========================================================================

  const selectOption = useCallback((option: ServiceOption) => {
    setBooking(prev => ({ ...prev, option }));
    setBookingStep('select-datetime');
    setDtSection('dates');
    speak(`${option.name} selected. Choose a date${option.sessionSlots?.length ? ' and session' : ' and time'}.`);
  }, [speak]);

  const handleConfirmBooking = useCallback(async () => {
    const { option, date, time, sessionLabel, sessionDuration, sessionPrice } = bookingRef.current;
    if (!option || !date || !time || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const datetime = new Date(`${date}T${time}:00`);
      const req: CreateBookingRequest = {
        serviceId: `${service.id}-${option.optionId}`,
        serviceName: `${service.title}: ${option.name}${sessionLabel ? ` (${sessionLabel})` : ''}`,
        category: service.category as string,
        datetime: datetime.toISOString(),
        durationMinutes: sessionDuration ?? option.durationMinutes ?? 60,
        price: sessionPrice !== null ? sessionPrice : option.price,
        location: service.location,
      };

      await onBookingComplete(req);
      setShowConfirmation(true);

      speak(
        `Your ${option.name}${sessionLabel ? ` — ${sessionLabel}` : ''} has been booked for ${formatDateDisplay(date)} at ${formatTimeDisplay(time)}. Enjoy!`,
        () => {
          if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
          confirmTimerRef.current = setTimeout(() => {
            confirmTimerRef.current = null;
            setBooking({ option: null, date: null, time: null, sessionLabel: null, sessionDuration: null, sessionPrice: null });
            setBookingStep('select-option');
            setShowConfirmation(false);
            onBack();
          }, 2000);
        }
      );
    } catch (err) {
      console.error('[StandardServiceDetailPage] booking failed:', err);
      speak('Sorry, there was a problem with your booking. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [isSubmitting, onBookingComplete, onBack, service, speak]);

  // Handle session-slot selection (Full Day / Morning / Afternoon)
  const handleSessionSelect = useCallback((slot: SessionSlot, idx: number, overrideDate?: string) => {
    const date = overrideDate ?? bookingRef.current.date ?? getDefaultDate();
    setFocusedSessionIndex(idx);
    setBooking(prev => ({
      ...prev,
      date,
      time: slot.time,
      sessionLabel: slot.label,
      sessionDuration: slot.durationMinutes,
      sessionPrice: slot.price,
    }));
    setTimeout(() => setBookingStep('confirm'), 0);
    speak(`${formatDateDisplay(date)}, ${slot.label}. Say confirm to book.`);
  }, [speak]);

  const handleCancel = useCallback(() => {
    if (bookingStep === 'confirm') {
      setBookingStep('select-datetime');
      setDtSection('dates');
    } else if (bookingStep === 'select-datetime') {
      setBooking(prev => ({ ...prev, option: null, date: null, time: null }));
      setBookingStep('select-option');
    } else {
      onBack();
    }
  }, [bookingStep, onBack]);

  // ==========================================================================
  // Voice Actions
  // ==========================================================================

  const handleVoiceAction = useCallback((action: VoiceAction, raw: string) => {
    const step = bookingStepRef.current;
    const bk = bookingRef.current;

    // Global cancel / back
    if (
      action.type === 'CANCEL' || action.type === 'CLOSE' ||
      /\b(cancel|quit|exit|close|nevermind|never\s*mind|forget\s*it)\b/i.test(raw)
    ) {
      speak('Going back.', () => handleCancel());
      return;
    }

    if (
      action.type === 'BACK' ||
      /\b(go\s*back|back|previous)\b/i.test(raw)
    ) {
      handleCancel();
      return;
    }

    // Confirm
    if (
      action.type === 'CONFIRM' || action.type === 'YES' ||
      /\b(yes|yeah|confirm|ok|perfect|book\s*it|do\s*it|proceed|sure)\b/i.test(raw)
    ) {
      if (step === 'confirm') {
        speak('Confirming your booking.');
        handleConfirmBooking();
      } else if (step === 'select-datetime' && bk.date && bk.time) {
        setBookingStep('confirm');
        speak(`${formatDateDisplay(bk.date)} at ${formatTimeDisplay(bk.time)}. Shall I confirm?`);
      }
      return;
    }

    // Global navigation commands — hand back to grid so the user can navigate.
    // Without this, saying "Open Spa" while in the Bar/Gym page would silently
    // fall through to the option-matching logic and give confusing feedback.
    if (
      action.type.startsWith('OPEN_') ||
      action.type === 'HOME'
    ) {
      speak('Going back to the menu.', () => onBack());
      return;
    }

    // BOOK_* actions carry a resolved option ID in their payload — use it
    // directly to select the right option instead of relying on fuzzy text match.
    // This handles: BOOK_TRANSPORT, BOOK_BAR, BOOK_POOL, BOOK_GYM
    if (
      action.type === 'BOOK_TRANSPORT' || action.type === 'BOOK_BAR' ||
      action.type === 'BOOK_POOL' || action.type === 'BOOK_GYM'
    ) {
      if (step === 'select-option') {
        const optionId = action.payload?.option;
        const matched = optionId
          ? availableOptions.find(o => o.optionId === optionId)
          : undefined;
        if (matched) {
          selectOption(matched);
          return;
        }
      }
      // Fall through to fuzzy text match below (handles edge cases)
    }

    // Option selection by name (step: select-option)
    if (step === 'select-option') {
      const lowerRaw = raw.toLowerCase();
      const matched = availableOptions.find(o =>
        lowerRaw.includes(o.name.toLowerCase()) ||
        o.name.toLowerCase().split(' ').some(w => w.length > 3 && lowerRaw.includes(w))
      );
      if (matched) {
        selectOption(matched);
        return;
      }
      speak(`Please select an option. Available: ${availableOptions.map(o => o.name).join(', ')}.`);
      return;
    }

    // Session slot selection (pool or any service with sessionSlots)
    if (step === 'select-datetime') {
      const optionSlots = bookingRef.current.option?.sessionSlots ?? service.sessionSlots;
      if (optionSlots?.length) {
        const lower = raw.toLowerCase();
        const sessionIdx = optionSlots.findIndex(s =>
          lower.includes(s.label.toLowerCase()) ||
          (s.id === 'full_day' && /full[\s-]*day/i.test(raw))
        );
        if (sessionIdx !== -1) {
          // Also extract a date from the same utterance (e.g. "tomorrow morning")
          const dateFromUtterance = parseDateFromVoice(raw) ?? undefined;
          handleSessionSelect(optionSlots[sessionIdx]!, sessionIdx, dateFromUtterance);
          return;
        }
      }
    }

    // Date / time parsing (steps: select-datetime or confirm)
    if (step === 'select-datetime' || step === 'confirm') {
      const parsedDate = parseDateFromVoice(raw);
      const parsedTimeRaw = parseTimeFromVoice(raw);
      const parsedTime = parsedTimeRaw ? snapToSlot(parsedTimeRaw) : null;

      // Compute synchronously against the ref — do NOT rely on the setBooking
      // updater callback (it runs async, so any flag set inside it is invisible
      // to code running after the setBooking() call on the same tick).
      if (parsedDate !== null || parsedTime !== null) {
        const newDate = parsedDate ?? bk.date;
        const newTime = parsedTime ?? bk.time;

        setBooking(prev => ({
          ...prev,
          date: parsedDate ?? prev.date,
          time: parsedTime ?? prev.time,
        }));

        if (newDate && newTime) {
          setTimeout(() => setBookingStep('confirm'), 0);
          speak(`${formatDateDisplay(newDate)} at ${formatTimeDisplay(newTime)}. Say confirm to book.`);
        } else if (newDate) {
          speak(`${formatDateDisplay(newDate)}. What time would you like?`);
        } else if (newTime) {
          speak(`${formatTimeDisplay(newTime)}. Which day?`);
        }
        return;
      }

      speak("Say a date and session, like 'tomorrow', 'morning', or 'full day'.");
    }
  }, [availableOptions, selectOption, handleConfirmBooking, handleCancel, handleSessionSelect, service.sessionSlots, speak, onBack]);

  // ==========================================================================
  // Voice recognition (push-to-talk, Space key)
  // ==========================================================================

  const startListening = useCallback(() => {
    if (!isSpeechRecognitionAvailable()) {
      setVoiceFeedback('Voice not supported');
      return;
    }
    zaxiaVoice.cancel();
    if (recognitionRef.current) recognitionRef.current.abort();

    const recognition = createSpeechRecognition();
    if (!recognition) return;

    recognitionRef.current = recognition;
    setVoiceState('listening');
    setVoiceTranscript('');
    transcriptRef.current = '';

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let interim = '';
      let final_ = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const r = event.results[i];
        if (r?.[0]) {
          if (r.isFinal) final_ += r[0].transcript;
          else interim += r[0].transcript;
        }
      }
      const t = final_ || interim;
      setVoiceTranscript(t);
      transcriptRef.current = t;
    };
    recognition.onerror = (e: Event) => {
      console.warn('[StandardServiceDetailPage] recognition error:', e);
      setVoiceState('idle');
    };
    recognition.onend = () => {
      // Browser can stop recognition on its own — ensure we leave 'listening' state
      setVoiceState(prev => prev === 'listening' ? 'idle' : prev);
    };
    recognition.start();
  }, []);

  const stopAndProcess = useCallback(() => {
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch { /* ignore */ }
      recognitionRef.current = null;
    }

    const text = transcriptRef.current.trim();
    if (!text) { setVoiceState('idle'); return; }

    setVoiceState('processing');
    const ctx = createVoiceContext('overlay', {});
    const action = processVoiceInput(text, ctx);
    console.log(`[StandardService] Processing voice: "${text}" → action:`, action.type);
    handleVoiceAction(action, text);

    setTimeout(() => {
      setVoiceState('idle');
      setVoiceTranscript('');
    }, 1500);
  }, [handleVoiceAction]);

  // ==========================================================================
  // Keyboard / remote navigation
  // ==========================================================================

  useEffect(() => {
    if (!isActive) return;

    const onKeyDown = (e: KeyboardEvent) => {
      // During the confirmation overlay, only allow Escape (exit early) and
      // Space (start voice so user can say "go home" etc.). Other keys like
      // Enter / arrows are suppressed so they don't re-trigger actions.
      if (showConfirmation) {
        if (e.key === 'Escape') {
          e.preventDefault();
          if (confirmTimerRef.current) {
            clearTimeout(confirmTimerRef.current);
            confirmTimerRef.current = null;
          }
          setShowConfirmation(false);
          setBooking({ option: null, date: null, time: null, sessionLabel: null, sessionDuration: null, sessionPrice: null });
          setBookingStep('select-option');
          onBack();
        } else if (e.code === 'Space' && !e.repeat) {
          e.preventDefault();
          e.stopPropagation();
          if (voiceState === 'idle') startListening();
        }
        return;
      }
      const tag = (document.activeElement as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;

      // Push-to-talk
      if (e.code === 'Space' && !e.repeat) {
        e.preventDefault();
        e.stopPropagation();
        if (voiceState === 'idle') startListening();
        return;
      }

      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault();
          if (bookingStep === 'select-option') {
            setFocusedOptionIndex(prev =>
              prev > 0 ? prev - 1 : availableOptions.length - 1
            );
          } else if (bookingStep === 'select-datetime') {
            if (dtSection === 'times' || dtSection === 'sessions') setDtSection('dates');
          }
          break;

        case 'ArrowDown':
          e.preventDefault();
          if (bookingStep === 'select-option') {
            setFocusedOptionIndex(prev =>
              prev < availableOptions.length - 1 ? prev + 1 : 0
            );
          } else if (bookingStep === 'select-datetime' && dtSection === 'dates') {
            const hasSlots = !!(booking.option?.sessionSlots ?? service.sessionSlots)?.length;
            setDtSection(hasSlots ? 'sessions' : 'times');
          } else if (bookingStep === 'select-datetime' && dtSection === 'sessions') {
            const slots = booking.option?.sessionSlots ?? service.sessionSlots ?? [];
            setFocusedSessionIndex(prev => (prev < slots.length - 1 ? prev + 1 : 0));
          }
          break;

        case 'ArrowLeft':
          e.preventDefault();
          if (bookingStep === 'select-datetime') {
            if (dtSection === 'dates') setFocusedDateIndex(prev => Math.max(0, prev - 1));
            else if (dtSection === 'times') setFocusedTimeIndex(prev => Math.max(0, prev - 1));
          } else if (bookingStep === 'confirm') {
            setConfirmFocusedButton('confirm');
          }
          break;

        case 'ArrowRight':
          e.preventDefault();
          if (bookingStep === 'select-datetime') {
            if (dtSection === 'dates') setFocusedDateIndex(prev => Math.min(availableDates.length - 1, prev + 1));
            else if (dtSection === 'times') setFocusedTimeIndex(prev => Math.min(TIME_SLOTS.length - 1, prev + 1));
          } else if (bookingStep === 'confirm') {
            setConfirmFocusedButton('cancel');
          }
          break;

        case 'Escape':
          e.preventDefault();
          // In select-datetime, Escape steps back within subsections before cancelling the step
          if (bookingStep === 'select-datetime' && (dtSection === 'times' || dtSection === 'sessions')) {
            setDtSection('dates');
          } else {
            handleCancel();
          }
          break;

        case 'Enter':
          e.preventDefault();
          if (bookingStep === 'select-option') {
            const opt = availableOptions[focusedOptionIndex];
            if (opt) selectOption(opt);
          } else if (bookingStep === 'select-datetime') {
            const hasSlots = !!(booking.option?.sessionSlots ?? service.sessionSlots)?.length;
            if (dtSection === 'dates') {
              // Commit the focused date and advance to times/sessions row
              const selDate = availableDates[focusedDateIndex];
              if (selDate) {
                setBooking(prev => ({ ...prev, date: selDate }));
                setDtSection(hasSlots ? 'sessions' : 'times');
                speak(`${formatDateDisplay(selDate)}. Now select a${hasSlots ? ' session' : ' time'}.`);
              }
            } else if (dtSection === 'times') {
              // Commit focused time and advance to confirm
              const selTime = TIME_SLOTS[focusedTimeIndex];
              const date = booking.date ?? availableDates[focusedDateIndex] ?? null;
              if (selTime && date) {
                setBooking(prev => ({ ...prev, time: selTime, date }));
                setTimeout(() => setBookingStep('confirm'), 0);
                speak(`${formatDateDisplay(date)} at ${formatTimeDisplay(selTime)}. Ready to confirm?`);
              }
            } else if (dtSection === 'sessions') {
              // Enter on a focused session — same as clicking it
              const slots = booking.option?.sessionSlots ?? service.sessionSlots ?? [];
              const slot = slots[focusedSessionIndex];
              if (slot) handleSessionSelect(slot, focusedSessionIndex);
            }
          } else if (bookingStep === 'confirm') {
            if (confirmFocusedButton === 'confirm') handleConfirmBooking();
            else handleCancel();
          }
          break;
      }
    };

    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space' && voiceState === 'listening') {
        e.preventDefault();
        e.stopPropagation();
        stopAndProcess();
      }
    };

    window.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('keyup', onKeyUp, true);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('keyup', onKeyUp, true);
    };
  }, [
    isActive, bookingStep, voiceState, showConfirmation,
    focusedOptionIndex, focusedDateIndex, focusedTimeIndex, focusedSessionIndex,
    dtSection, availableOptions, availableDates, booking.option, booking.date, service.sessionSlots,
    confirmFocusedButton,
    selectOption, handleConfirmBooking, handleCancel, handleSessionSelect, onBack,
    startListening, stopAndProcess, speak,
  ]);

  // Focus on mount
  useEffect(() => {
    if (isActive && containerRef.current) containerRef.current.focus();
  }, [isActive]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) { try { recognitionRef.current.abort(); } catch { /* */ } }
      zaxiaVoice.cancel();
      // Cancel any pending post-confirmation navigation timer.
      if (confirmTimerRef.current) {
        clearTimeout(confirmTimerRef.current);
        confirmTimerRef.current = null;
      }
    };
  }, []);

  // ==========================================================================
  // MAIN RENDER
  // ==========================================================================

  return (
    <motion.div
      ref={containerRef}
      tabIndex={-1}
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      className="standard-service-detail h-full flex flex-col outline-none relative"
    >
      {/* ── Content ── */}
      <div className="flex-1 overflow-hidden flex min-h-0">

        {/* Image slideshow — left panel */}
        <div className="w-2/5 relative overflow-hidden flex-shrink-0">
          <AnimatePresence mode="wait">
            <img
              key={currentImageIndex}
              src={service.images[currentImageIndex]}
              alt={service.title}
              className="absolute inset-0 w-full h-full object-cover slide-img-fade"
            />
          </AnimatePresence>

          {/* Gradient + info overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/60" />

          {service.images.length > 1 && (
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
              {service.images.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentImageIndex(idx)}
                  className={clsx(
                    'h-1.5 rounded-full transition-all',
                    idx === currentImageIndex ? 'bg-white/80 w-6' : 'bg-white/30 w-1.5 hover:bg-white/50'
                  )}
                />
              ))}
            </div>
          )}

          {/* Description pinned at bottom */}
          <div className="absolute bottom-0 left-0 right-0 p-5 bg-gradient-to-t from-black/80 to-transparent">
            <p className="text-white/70 text-xs leading-relaxed line-clamp-3">
              {service.longDescription}
            </p>
            {service.location && (
              <p className="text-white/40 text-xs mt-2">📍 {service.location}</p>
            )}
          </div>
        </div>

        {/* Right panel — booking steps */}
        <div className="std-content-panel flex-1 flex flex-col overflow-hidden px-6 py-4">
          <AnimatePresence mode="wait">

            {/* ── Step 1: Select option ── */}
            {bookingStep === 'select-option' && (
              <motion.div
                key="select-option"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="flex-1 flex flex-col"
              >
                <h3 className="text-sm text-white/50 uppercase tracking-wider mb-4">
                  Select an Option
                </h3>
                <div className="flex-1 space-y-3 overflow-y-auto hide-scrollbar px-1 py-1">
                  {availableOptions.map((option, idx) => {
                    const isFocused = focusedOptionIndex === idx;
                    return (
                      <motion.div
                        key={option.optionId}
                        className={clsx(
                          'p-5 rounded-2xl border cursor-pointer transition-all',
                          isFocused
                            ? 'bg-teal-500/10 border-teal-500/30'
                            : 'bg-white/[0.02] border-white/[0.04] hover:bg-white/[0.04]'
                        )}
                        animate={{ scale: isFocused ? 1.01 : 1 }}
                        onClick={() => {
                          setFocusedOptionIndex(idx);
                          selectOption(option);
                        }}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="text-base font-medium text-white">{option.name}</h4>
                            <p className="text-sm text-white/40 mt-1 line-clamp-2">{option.description}</p>
                            {option.durationMinutes && (
                              <p className="text-xs text-white/30 mt-1">
                                ⏱ {formatDuration(option.durationMinutes)}
                              </p>
                            )}
                          </div>
                          <div className="text-right ml-4 flex-shrink-0">
                            {option.price != null ? (
                              <div className="text-lg font-light text-teal-400">
                                {option.sessionSlots?.length ? 'from ' : ''}{formatCurrency(option.price)}
                              </div>
                            ) : (
                              <div className="text-lg font-light text-green-400">Free</div>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </motion.div>
            )}

            {/* ── Step 2: Select date & time ── */}
            {bookingStep === 'select-datetime' && (
              <motion.div
                key="select-datetime"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="flex-1 flex flex-col"
              >
                {/* Selected option summary */}
                <div className="mb-5 p-4 rounded-xl bg-teal-500/10 border border-teal-500/20 flex-shrink-0">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-white font-medium">{booking.option?.name}</div>
                      {booking.option?.durationMinutes && !booking.option.sessionSlots?.length && (
                        <div className="text-xs text-white/40 mt-0.5">
                          {formatDuration(booking.option.durationMinutes)}
                        </div>
                      )}
                    </div>
                    {(() => {
                      // For session-based options, only show price once a slot is picked
                      const hasSlots = !!booking.option?.sessionSlots?.length;
                      const displayPrice = booking.sessionPrice !== null
                        ? booking.sessionPrice
                        : hasSlots ? null : (booking.option?.price ?? null);
                      if (displayPrice == null && hasSlots) return null;
                      return displayPrice != null ? (
                        <div className="text-teal-400 font-light text-lg">
                          {formatCurrency(displayPrice)}
                        </div>
                      ) : (
                        <div className="text-green-400 font-light text-lg">Free</div>
                      );
                    })()}
                  </div>
                </div>

                {/* Date selection */}
                <h3 className="text-sm text-white/50 uppercase tracking-wider mb-3 flex items-center gap-2 flex-shrink-0">
                  <Calendar className="w-4 h-4" />
                  Select Date
                </h3>
                <div className="flex gap-2 overflow-x-auto px-1 py-1 pb-3 flex-shrink-0 hide-scrollbar">
                  {availableDates.map((date, idx) => (
                    <button
                      key={date}
                      onClick={() => {
                        setFocusedDateIndex(idx);
                        setBooking(prev => ({ ...prev, date }));
                        speak(`${formatDateDisplay(date)}. Now select a${(booking.option?.sessionSlots ?? service.sessionSlots)?.length ? ' session' : ' time'}.`);
                      }}
                      className={clsx(
                        'px-4 py-2 rounded-xl whitespace-nowrap text-sm transition-all flex-shrink-0',
                        idx === focusedDateIndex && booking.date === date
                          ? 'bg-teal-600 text-white'
                          : booking.date === date
                          ? 'bg-teal-500/20 text-teal-300 border border-teal-500/30'
                          : idx === focusedDateIndex && dtSection === 'dates'
                          ? 'bg-white/20 text-white ring-2 ring-teal-500'
                          : 'bg-white/[0.06] text-white/70 hover:bg-white/[0.12]'
                      )}
                    >
                      {formatDateDisplay(date)}
                    </button>
                  ))}
                </div>

                {/* Time slots — or session slots when service defines fixed sessions (e.g. pool) */}
                {(() => {
                  const slots = booking.option?.sessionSlots ?? service.sessionSlots;
                  return slots?.length ? (
                    <>
                      <h3 className="text-sm text-white/50 uppercase tracking-wider mb-3 mt-4 flex items-center gap-2 flex-shrink-0">
                        <Clock className="w-4 h-4" />
                        Select Session
                      </h3>
                      <div className="flex-1 flex flex-col gap-3">
                        {slots.map((slot, idx) => (
                        <motion.button
                          key={slot.id}
                          onClick={() => handleSessionSelect(slot, idx)}
                          className={clsx(
                            'flex items-center justify-between px-5 py-4 rounded-2xl border transition-all text-left',
                            focusedSessionIndex === idx
                              ? 'bg-teal-500/10 border-teal-500/30'
                              : 'bg-white/[0.03] border-white/[0.06] hover:bg-white/[0.06]'
                          )}
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-2xl">{slot.icon}</span>
                            <div>
                              <div className="text-white font-medium">{slot.label}</div>
                              <div className="text-white/40 text-xs mt-0.5">{slot.durationLabel}</div>
                            </div>
                          </div>
                          <span className="text-teal-400 text-sm font-light">
                            {slot.price != null ? formatCurrency(slot.price) : 'Free'}
                          </span>
                        </motion.button>
                      ))}
                    </div>
                    <p className="text-xs text-white/30 mt-3 text-center flex-shrink-0">
                      Or say “full day”, “morning”, or “afternoon”
                    </p>
                  </>
                ) : (
                  <>
                    {/* Time slots */}
                    <h3 className="text-sm text-white/50 uppercase tracking-wider mb-3 mt-4 flex items-center gap-2 flex-shrink-0">
                      <Clock className="w-4 h-4" />
                      Select Time
                    </h3>
                    <div className="flex-1 overflow-y-auto min-h-0 px-1 py-1">
                      <div className="grid grid-cols-4 gap-2">
                        {TIME_SLOTS.map((time, idx) => (
                          <button
                            key={time}
                            onClick={() => {
                              setFocusedTimeIndex(idx);
                              const date = booking.date ?? getDefaultDate();
                              setBooking(prev => ({ ...prev, time, date }));
                              // Advance to confirm step when both are set
                              setTimeout(() => {
                                const d = booking.date ?? date;
                                setBookingStep('confirm');
                                speak(`${formatDateDisplay(d)} at ${formatTimeDisplay(time)}. Ready to confirm?`);
                              }, 0);
                            }}
                            className={clsx(
                              'py-2.5 rounded-xl text-sm transition-all',
                              idx === focusedTimeIndex && booking.time === time
                                ? 'bg-teal-600 text-white'
                                : booking.time === time
                                ? 'bg-teal-500/20 text-teal-300 border border-teal-500/30'
                                : idx === focusedTimeIndex && dtSection === 'times'
                                ? 'bg-white/20 text-white ring-2 ring-teal-500'
                                : 'bg-white/[0.04] text-white/60 hover:bg-white/[0.08] border border-white/[0.04]'
                            )}
                          >
                            {formatTimeDisplay(time)}
                          </button>
                        ))}
                      </div>
                    </div>
                    {/* Voice hint */}
                    <p className="text-xs text-white/30 mt-3 text-center flex-shrink-0">
                      Or say “tomorrow at 4 PM”
                    </p>
                  </>
                );
                })()}
              </motion.div>
            )}

            {/* ── Step 3: Confirm ── */}
            {bookingStep === 'confirm' && (
              <motion.div
                key="confirm"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="flex-1 flex flex-col items-center justify-center"
              >
                <div className="w-16 h-16 rounded-full bg-teal-500/10 flex items-center justify-center mb-6 text-3xl">
                  {service.icon}
                </div>

                <h3 className="text-xl font-light text-white mb-6">Confirm Your Booking</h3>

                <div className="w-full max-w-sm space-y-4">
                  {/* Option */}
                  <div className="p-4 rounded-xl bg-white/[0.03] border border-white/[0.06]">
                    <div className="text-center">
                      <div className="text-white font-medium text-lg">{booking.option?.name}</div>
                      {booking.option?.durationMinutes && (
                        <div className="text-white/40 text-sm mt-1">
                          {formatDuration(booking.option.durationMinutes)}
                        </div>
                      )}
                      {booking.option?.description && (
                        <div className="text-white/30 text-xs mt-1">{booking.option.description}</div>
                      )}
                    </div>
                  </div>

                  {/* Date & Session / Time */}
                  <div className="p-4 rounded-xl bg-white/[0.03] border border-white/[0.06]">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-white/60 text-sm">
                        <Calendar className="w-4 h-4" />
                        {booking.date && formatDateDisplay(booking.date)}
                      </div>
                      <div className="flex items-center gap-2 text-white/60 text-sm">
                        <Clock className="w-4 h-4" />
                        {booking.sessionLabel ?? (booking.time && formatTimeDisplay(booking.time))}
                      </div>
                    </div>
                  </div>

                  {/* Price */}
                  <div className="p-4 rounded-xl bg-teal-500/10 border border-teal-500/20">
                    <div className="flex items-center justify-between">
                      <span className="text-white/60 text-sm">Total</span>
                      {(() => {
                        const price = booking.sessionPrice !== null
                          ? booking.sessionPrice
                          : booking.option?.price ?? null;
                        return (
                          <span className={clsx('text-xl font-light',
                            price != null ? 'text-teal-400' : 'text-green-400'
                          )}>
                            {price != null ? formatCurrency(price) : 'Free'}
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex gap-3 mt-8">
                  <button
                    onClick={handleCancel}
                    className={clsx(
                      'px-6 py-2.5 rounded-xl bg-white/[0.04] hover:bg-white/[0.08] text-white/60 transition-colors flex items-center gap-2',
                      confirmFocusedButton === 'cancel' && 'ring-2 ring-white/50 bg-white/[0.08]'
                    )}
                  >
                    <X className="w-4 h-4" />
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmBooking}
                    disabled={isSubmitting}
                    className={clsx(
                      'px-8 py-2.5 rounded-xl transition-all flex items-center gap-2',
                      isSubmitting
                        ? 'bg-white/[0.08] text-white/40 cursor-not-allowed'
                        : confirmFocusedButton === 'confirm'
                        ? 'bg-teal-500/30 text-teal-300 ring-2 ring-teal-400'
                        : 'bg-teal-500/20 hover:bg-teal-500/30 text-teal-400'
                    )}
                  >
                    {isSubmitting ? (
                      'Booking...'
                    ) : (
                      <>
                        <Check className="w-4 h-4" />
                        Confirm Booking
                      </>
                    )}
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* ── Footer: voice + keyboard hints ── */}
      <div className="px-6 py-3 border-t border-white/[0.04] flex-shrink-0">
        {/* Voice feedback bar */}
        <AnimatePresence>
          {(voiceState !== 'idle' || voiceFeedback) && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              className="flex items-center justify-center gap-3 mb-3"
            >
              {voiceState === 'listening' && (
                <>
                  <motion.div
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ repeat: Infinity, duration: 1.2, ease: 'easeInOut' }}
                    className="w-8 h-8 rounded-full bg-teal-500/20 flex items-center justify-center"
                  >
                    <Mic className="w-4 h-4 text-teal-400" />
                  </motion.div>
                  <span className="text-sm text-white/60">
                    {voiceTranscript || 'Listening…'}
                  </span>
                </>
              )}
              {voiceState === 'processing' && (
                <span className="text-sm text-white/50">Processing…</span>
              )}
              {voiceState === 'idle' && voiceFeedback && (
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-sm text-teal-400 italic max-w-xs text-center"
                >
                  {voiceFeedback}
                </motion.span>
              )}
            </motion.div>
          )}
        </AnimatePresence>

      </div>

      {/* ── Success overlay ── */}
      <AnimatePresence>
        {showConfirmation && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/80 flex items-center justify-center z-50"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="text-center"
            >
              <div className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-6">
                <Check className="w-10 h-10 text-emerald-400" />
              </div>
              <h2 className="text-2xl font-light text-white mb-2">Booking Confirmed</h2>
              <p className="text-white/60 text-center text-lg">{booking.option?.name}</p>
              <p className="text-white/40 text-sm mt-2">
                {booking.date && formatDateDisplay(booking.date)}
                {' · '}
                {booking.sessionLabel ?? (booking.time && formatTimeDisplay(booking.time))}
              </p>
              {(() => {
                const price = booking.sessionPrice !== null
                  ? booking.sessionPrice
                  : booking.option?.price ?? null;
                const duration = booking.sessionDuration ?? booking.option?.durationMinutes ?? null;
                if (price == null) return null;
                return (
                  <p className="text-emerald-400/80 text-sm mt-2">
                    {formatCurrency(price)}
                    {duration != null && ` · ${formatDuration(duration)}`}
                  </p>
                );
              })()}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default StandardServiceDetailPage;
